import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { AditionInitialization, AditionTagWithFallback } from '@vgn-medien-holding/vgn-fe-components';
import { getCookie } from 'cookies-next';
import { Meta } from '@components/atoms/MetaTags/Meta';
import { Oewa } from '@components/atoms/Oewa/Oewa';
import { EntryCarousel } from '@components/molecules/EntryCarousel/EntryCarousel';
import { EntityMix } from '@components/organisms/EntityMix/EntityMix';
import { providers } from '@utils/providers';
import { GetGenresDocument } from '@src/lib/graphql/generated';
import { createUrqlClient } from '@src/lib/graphql/urql';
import { useTagManager } from '@lib/hooks/useTagManager';

// Genres
const ad_settings_Genres = {
  billboard: {
    id: 5256675,
    name: 'TVM_Genres_Billboard',
    yieldLab: [],
    breakpoints: [3, 4, 5, 6],
  },
  mobile_middle1: {
    id: 5256603,
    name: 'TVM_Mobile_Genres_MedRec_Cad',
    yieldLab: [],
    breakpoints: [0, 1, 2, 3],
  },
};

export function GenreDetails({ data }) {
  const { genres, oewa } = data;
  const [activeGenre, setActiveGenre] = useState(data?.activeGenre);
  const [genreTarget, setGenreTarget] = useState(data?.activeGenre?.id || genres?.[0]);
  const [sortOrder, setSortOrder] = useState(null);
  const router = useRouter();
  const [filters, setFilters] = useState({
    entityType: null,
    provider: null,
  });

  useEffect(() => {
    if (router.isReady) {
      setFilters({
        entityType: router.query.entityType,
        provider: providers?.find((e) => e.id === router.query.provider)?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  useEffect(() => {
    if (router.isReady && activeGenre?.slug) {
      router.replace(
        {
          pathname: '/genres/[slug]',
          query: {
            slug: activeGenre?.slug,
            ...(filters?.entityType && { entityType: filters?.entityType }),
            ...(filters?.provider && { provider: filters?.provider }),
          },
        },
        null,
        { shallow: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeGenre]);

  useTagManager({
    event: 'metadata',
    loginStatus: 'nicht eingeloggt',
    seitentyp: 'Genre',
    titel: activeGenre?.title,
    assetID: oewa?.id,
    aktualisierungsdatum: data?.updated_at,
    erstellungsdatum: data?.created_at,
    oewaSeitenkontingent: oewa?.page_key || 'Service',
    oewaSubkontingent: oewa?.category_key || 'Programmeundlokalfuehrer',
    oewaProfilingkontingent: oewa?.profile_key || 'TVProgramme',
    cookieAlert: getCookie('OptanonConsent'),
  });

  return (
    <>
      <AditionInitialization data={{ ad_settings: ad_settings_Genres }} />
      <Oewa
        pageKey={oewa?.page_key || 'RedCont'}
        categoryKey={oewa?.category_key || 'KulturUndFreizeit'}
        profileKey={oewa?.profile_key || 'FilmUndKino'}
      />
      <Head>
        <title>{activeGenre?.title}</title>
        {Meta(activeGenre?.metadata)}
      </Head>
      <div className="grid">
        <div className="row-start-3 mt-12 grid min-w-0 gap-2 lg:row-start-auto lg:mt-0 lg:flex lg:flex-wrap lg:justify-center lg:gap-4 lg:px-3">
          <select
            className="input-tvm-classic lg:giant"
            value={activeGenre?.id || ''}
            onChange={(item) => {
              setGenreTarget(item?.target.value);
            }}
          >
            {genres?.data?.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.title}
              </option>
            ))}
          </select>
          <select
            className="input-tvm-classic lg:giant"
            value={filters.entityType || ''}
            onChange={(item) => {
              setFilters((prev) => ({ ...prev, entityType: item?.target.value }));
            }}
          >
            <option value="all">Alle Inhalte</option>
            <option value="movies">Filme</option>
            <option value="shows">Serien</option>
          </select>
          <div className="hidden min-w-0 items-center px-2 text-2xl font-semibold text-gray-500 lg:grid">auf</div>
          <select
            className="input-tvm-classic lg:giant"
            value={filters.provider || ''}
            onChange={(item) => {
              setFilters((prev) => ({ ...prev, provider: item?.target.value }));
            }}
          >
            <option value="">Alle Anbieter</option>
            {providers?.map((provider) => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
        </div>
        <div className="row-start-1 mt-1 min-w-0 lg:row-start-auto">
          {genres?.data?.length > 0 && (
            <EntryCarousel
              target={genreTarget}
              type="text"
              primaryProperty="title"
              entries={genres.data}
              classProps={{ carouselRoot: 'aspect-square min-h-[300px] h-[40vh] text-6xl' }}
              onEntryChange={(genre) => {
                setGenreTarget(null);
                setActiveGenre(genre);
              }}
            />
          )}
        </div>
        <aside>
          <AditionTagWithFallback
            tag={ad_settings_Genres.billboard}
            fallback={ad_settings_Genres.mobile_middle1}
            breakpoint={'lg'}
            classProps={{ root: 'my-5 grid place-items-center' }}
          />
        </aside>
        <div className="mx-auto mt-4 max-w-screen-md px-8 text-center sm:mt-0">
          <p className="font-bold">
            Hier findest Du eine komplette Liste aller Filme und Serien des Genres {activeGenre?.title}.
          </p>
          <p>
            Klicke auf “Sortieren” und reihe die Liste nach Popularität, Redaktionsbewertung, IMDb-Bewertung,
            Veröffentlichung oder alphabetisch. Mit “Filtern” kannst Du die Filme und Serien nach ähnlichen Kriterien
            aussortieren.
          </p>
        </div>
        <div className="mt-2 grid lg:mt-24 lg:block">
          <select
            className="input-tvm-classic"
            defaultValue='{"orderBy": "tmdb_score", "orderDirection": "DESC"}'
            onChange={(item) => {
              try {
                setSortOrder(JSON.parse(item?.target.value));
              } catch (err) {
                setSortOrder({ orderBy: 'null', orderDirection: 'null' });
              }
            }}
          >
            <option value='{"orderBy": "tmdb_score", "orderDirection": "DESC"}'>Sortieren</option>
            <option value='{"orderBy": "year", "orderDirection": "DESC"}'>Jahr absteigend</option>
            <option value='{"orderBy": "year", "orderDirection": "ASC"}'>Jahr aufsteigend</option>
            <option value='{"orderBy": "title", "orderDirection": "DESC"}'>Title absteigend</option>
            <option value='{"orderBy": "title", "orderDirection": "ASC"}'>Title aufsteigend</option>
          </select>
        </div>
      </div>
      <article className="mt-10">
        <EntityMix
          movieVariables={{
            first: 20,
            page: 1,
            genres: activeGenre ? [activeGenre?.id] : [],
            provider: filters.provider ? [filters.provider] : [],
            orderBy: sortOrder?.orderBy,
            orderDirection: sortOrder?.orderDirection,
          }}
          movieSkip={filters?.entityType === 'shows' || !activeGenre}
          tvshowVariables={{
            first: 20,
            page: 1,
            genres: activeGenre ? [activeGenre?.id] : [],
            provider: filters.provider ? [filters.provider] : [],
            orderBy: sortOrder?.orderBy,
            orderDirection: sortOrder?.orderDirection,
          }}
          tvshowSkip={filters?.entityType === 'movies' || !activeGenre}
        >
          <div className="py-8 text-center text-2xl">Leider liegen für diese Auswahl momentan keine Inhalte vor.</div>
        </EntityMix>
      </article>
    </>
  );
}

export const genreDetailsSsrLogic = async ({ slug }) => {
  const { ssrQuery, ssrCache } = createUrqlClient();
  const { data: genresData } = await ssrQuery({
    query: GetGenresDocument,
    variables: { first: 100, page: 1 },
  });
  const genres = genresData?.genres;
  const activeGenre = genres?.data?.find((e) => e.slug === slug);

  const data = {
    genres,
    activeGenre: activeGenre || null,
  };

  return {
    revalidate: 60 * 60 * 24 * 2, // 2 days
    props: {
      data,
      urqlState: ssrCache.extractData(),
    },
  };
};
